// Phase 3.x (beads hh-iwc) — EquipmentPlacePicker.
//
// A reusable, collapsed-by-default disclosure picker for *equipment* location.
// Unlike `PlacePicker` (used by item forms, which always yields propertyId +
// roomId), equipment can live either inside a room OR directly at the
// property level ("вся собственность"). The selected value is therefore a
// tagged union — `{ kind: 'room' | 'property', id }`.
//
// Used by:
//   - features/forms/EditEquipmentScreen.tsx
//   - features/equipment-add/CatalogEquipmentScreen.tsx
//   - features/equipment-add/OutOfCatalogScreen.tsx
//
// UI: a single summary row showing the current selection; tapping it expands
// a hierarchy tree (property as parent, a selectable "вся собственность" node
// plus nested rooms). Picking a node collapses the disclosure back. The
// collapsed control does not occupy the whole screen.

import type { JSX } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { T, Icon, entityIcon } from '../../tokens';
import type { PropertyTreeNode } from '../../api/endpoints/properties.js';

/** A place equipment can live — a room, or a property directly. */
export interface EquipmentPlaceSelection {
  readonly kind: 'room' | 'property';
  readonly id: string;
}

export interface EquipmentPlacePickerProps {
  readonly tree: readonly PropertyTreeNode[];
  readonly value: EquipmentPlaceSelection | null;
  readonly onChange: (sel: EquipmentPlaceSelection) => void;
  readonly disabled?: boolean;
}

/** Two selections are equal iff both kind and id match. */
function sameSelection(
  a: EquipmentPlaceSelection | null,
  b: EquipmentPlaceSelection,
): boolean {
  return a !== null && a.kind === b.kind && a.id === b.id;
}

/** Resolve a selection into a human label, e.g. "Дача → Котельная". */
function describeSelection(
  tree: readonly PropertyTreeNode[],
  value: EquipmentPlaceSelection | null,
): { label: string; sublabel: string } | null {
  if (value === null) return null;
  for (const property of tree) {
    if (value.kind === 'property' && property.id === value.id) {
      return { label: property.name, sublabel: 'вся собственность' };
    }
    if (value.kind === 'room') {
      for (const room of property.rooms) {
        if (room.id === value.id) {
          return { label: room.name, sublabel: property.name };
        }
      }
    }
  }
  return null;
}

/** Name of the property a selection points at — for the summary-row icon. */
function selectedPropertyName(
  tree: readonly PropertyTreeNode[],
  value: EquipmentPlaceSelection | null,
): string | null {
  if (value === null) return null;
  for (const property of tree) {
    if (value.kind === 'property' && property.id === value.id) return property.name;
    if (value.kind === 'room') {
      for (const room of property.rooms) {
        if (room.id === value.id) return property.name;
      }
    }
  }
  return null;
}

export function EquipmentPlacePicker({
  tree,
  value,
  onChange,
  disabled = false,
}: EquipmentPlacePickerProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);

  const summary = useMemo(() => describeSelection(tree, value), [tree, value]);
  // Название собственности, на которую указывает выбор — для иконки строки.
  const summaryPropertyName = useMemo(
    () => selectedPropertyName(tree, value),
    [tree, value],
  );

  if (tree.length === 0) {
    return (
      <div
        style={{
          padding: 14,
          background: T.surfaceAlt,
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          fontSize: 12.5,
          color: T.ink3,
        }}
      >
        Нет собственности — сначала добавь хотя бы одну.
      </div>
    );
  }

  const select = (sel: EquipmentPlaceSelection): void => {
    onChange(sel);
    setOpen(false);
  };

  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${open ? T.brandSoft2 : T.border}`,
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {/* Summary row — collapsed control. */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: 'transparent',
          border: 'none',
          fontFamily: T.font,
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          textAlign: 'left',
        }}
      >
        {/* для собственности — иконка по названию; для комнаты — sliders. */}
        <Icon
          name={
            value?.kind === 'room'
              ? 'sliders'
              : entityIcon('property', { name: summaryPropertyName })
          }
          size={18}
          color={summary !== null ? T.brandDeep : T.ink3}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {summary !== null ? (
            <>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>
                {summary.label}
              </div>
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 1 }}>
                {summary.sublabel}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink3 }}>
              Выбери место
            </div>
          )}
        </div>
        <Icon name={open ? 'chevD' : 'chevR'} size={18} color={T.ink3} />
      </button>

      {/* Expanded tree. */}
      {open && (
        <div style={{ borderTop: `1px solid ${T.divider}` }}>
          {tree.map((property, pi) => {
            const propSel: EquipmentPlaceSelection = {
              kind: 'property',
              id: property.id,
            };
            const propActive = sameSelection(value, propSel);
            return (
              <div key={property.id}>
                {pi > 0 && <div style={{ height: 1, background: T.divider }} />}

                {/* Property header — selectable "вся собственность" node. */}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => select(propSel)}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    background: propActive ? T.brandSoft : 'transparent',
                    border: 'none',
                    fontFamily: T.font,
                    cursor: disabled ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    textAlign: 'left',
                  }}
                >
                  <Icon
                    name={entityIcon('property', { name: property.name })}
                    size={17}
                    color={propActive ? T.brandDeep : T.ink2}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: propActive ? T.brandDeep : T.ink,
                      }}
                    >
                      {property.name}
                    </div>
                    <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>
                      вся собственность
                    </div>
                  </div>
                  {propActive && <Icon name="check" size={16} color={T.brandDeep} />}
                </button>

                {/* Nested rooms. */}
                {property.rooms.map((room) => {
                  const roomSel: EquipmentPlaceSelection = {
                    kind: 'room',
                    id: room.id,
                  };
                  const roomActive = sameSelection(value, roomSel);
                  return (
                    <button
                      key={room.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => select(roomSel)}
                      style={{
                        width: '100%',
                        padding: '8px 12px 8px 0',
                        background: roomActive ? T.brandSoft : 'transparent',
                        border: 'none',
                        fontFamily: T.font,
                        cursor: disabled ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        textAlign: 'left',
                      }}
                    >
                      {/* Indent guide marks the room as nested under property. */}
                      <div
                        style={{
                          width: 30,
                          alignSelf: 'stretch',
                          flexShrink: 0,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <div style={{ width: 1.5, background: T.border }} />
                      </div>
                      <Icon
                        name="sliders"
                        size={16}
                        color={roomActive ? T.brandDeep : T.ink3}
                      />
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 13,
                          fontWeight: 600,
                          color: roomActive ? T.brandDeep : T.ink,
                        }}
                      >
                        {room.name}
                      </div>
                      {roomActive && (
                        <Icon name="check" size={16} color={T.brandDeep} />
                      )}
                      <div style={{ width: 12, flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
